import { Base } from '@studiometa/js-toolkit';
import { getOffsetSizes, loadImage, nextFrame } from '@studiometa/js-toolkit/utils';
import gsap from 'gsap';

/**
 * ArchivesGalleryItem class.
 * @augments {Base}
 */
export default class ArchivesGalleryItem extends Base {
  /**
   * IntersectionObserver instance pour détecter la visibilité de l'élément.
   * @type {IntersectionObserver|null}
   */
  $observer = null;

  /**
   * Configuration statique de la classe.
   * @type {object}
   * @property {string} name - Nom du composant.
   * @property {string[]} emits - Événements émis.
   * @property {string[]} refs - Références aux éléments DOM.
   * @property {object} options - Options du composant.
   * @property {object} options.index - Index de l'élément.
   * @property {object} options.column - Colonne de l'élément.
   * @property {object} options.row - Ligne de l'élément.
   * @property {object} options.clone - Numéro du clone.
   */
  static config = {
    name: 'ArchivesGalleryItem',
    emits: ['change', 'intersect'],
    refs: ['image'],
    options: {
      index: { type: Number, default: 0 },
      column: { type: Number, default: 0 },
      row: { type: Number, default: 0 },
      clone: { type: Number, default: 0 },
    },
  };

  /**
   * Charge l'image de l'élément.
   * @returns {Promise<void>} Promise résolue lorsque l'image est chargée ou supprimée.
   */
  loadImage() {
    return loadImage(this.$refs.image.dataset.src)
      .then(() => {
        this.$refs.image.src = this.$refs.image.dataset.src;
        this.$refs.image.classList.add('is-loaded');
      })
      .catch(() => {
        this.$refs.image.remove();
        return Promise.resolve();
      });
  }

  /**
   * Indique si l'élément est actuellement actif (agrandi).
   * @type {boolean}
   */
  isActive = false;

  /**
   * Indique si l'élément est visible dans le viewport.
   * @type {boolean}
   */
  isVisible = true;

  /**
   * Liste des instances du même élément (clones).
   * @type {ArchivesGalleryItem[]}
   */
  instances = [];

  /**
   * Liste des éléments DOM correspondant aux instances.
   * @type {HTMLElement[]}
   */
  elements = [];

  /**
   * Position calculée de l'élément.
   * @type {{x: number|null, y: number|null}}
   */
  position = { x: null, y: null };

  /**
   * Animation GSAP en cours.
   * @type {gsap.core.Tween|null}
   */
  animation = null;

  /**
   * Options de configuration de l'IntersectionObserver.
   * @type {IntersectionObserverInit|null}
   * @private
   */
  _observerOptions = null;

  /**
   * Animation d'entrée de l'élément dans la galerie.
   * @returns {Promise<void>} Promise résolue lorsque l'animation est terminée.
   */
  async enter() {
    this.animation?.kill();
    this.isActive = false;
    await new Promise((resolve) => {
      const isRandomMode = this.$parent.mode === 'random';
      const duration = this.isVisible ? 0.6 : 0;

      if (isRandomMode) {
        const initialX = Math.random() * 100 - 50;
        const initialY = Math.random() * 100 - 50;

        this.animation = gsap.fromTo(
          this.elements,
          {
            rotation: 0,
            x: 500,
            y: 500,
            opacity: 0,
            scale: 1,
          },
          {
            rotation: Math.random() * 160 - 80,
            x: initialX,
            y: initialY,
            scale: 1,
            opacity: 1,
            zIndex: Math.floor(Math.random() * 3),
            duration,
            ease: 'power4',
            onComplete: resolve,
          },
        );
      } else {
        this.animation = gsap.fromTo(
          this.elements,
          {
            rotation: 0,
            x: 500,
            y: 500,
            opacity: 0,
          },
          {
            x: 0,
            y: 0,
            rotation: 0,
            zIndex: 0,
            opacity: 1,
            duration,
            ease: 'power4',
            onComplete: resolve,
          },
        );
      }
    });
  }

  /**
   * Animation de sortie de l'élément de la galerie.
   * @returns {Promise<void>} Promise résolue lorsque l'animation est terminée.
   */
  async leave() {
    this.animation?.kill();
    this.isActive = false;
    await new Promise((resolve) => {
      if (this.isVisible) {
        this.animation = gsap.to(this.elements, {
          x: -500,
          y: -500,
          opacity: 0,
          duration: 0.3,
          ease: 'power4',
          onComplete: () => {
            gsap.set(this.$el, {
              x: 0,
              y: 0,
              opacity: 0,
              onComplete: resolve,
            });
          },
        });
      } else {
        this.animation = gsap.set(this.elements, {
          x: 0,
          y: 0,
          opacity: 0,
          onComplete: resolve,
        });
      }
    });
  }

  /**
   * Méthode appelée lorsque le composant est monté.
   * Initialise l'IntersectionObserver et les écouteurs d'événements.
   * @returns {void}
   */
  mounted() {
    this._observerOptions = {
      root: this.$parent.$el,
      rootMargin: '200px',
      threshold: 0.1,
    };

    this.$observer = new IntersectionObserver((entries) => {
      this.__callMethod('intersected', entries);
    }, this._observerOptions);

    this.$observer.observe(this.$el);

    this.instances = this.$parent.$children.ArchivesGalleryItem.filter(
      (item) => item.$options.index === this.$options.index,
    ).sort((a, b) => a.$options.clone - b.$options.clone);
    this.elements = this.instances.map(({ $el }) => $el);

    this.$parent.$on('move', () => {
      if (this.isActive) {
        this.popOut();
        this.$emit('change', false);
      }
    });

    this.$parent.$on('mode', () => {
      this.init();
    });

    this.init();
  }

  /**
   * Initialise l'élément : calcule les positions et démarre l'animation d'entrée.
   * @returns {Promise<void>} Promise résolue lorsque l'initialisation est terminée.
   */
  async init() {
    if (this.isActive) {
      await this.instances[0].popOut();
    }

    this.resized();

    const itemRect = this.$el.getBoundingClientRect();
    const rootRect = this._observerOptions.root.getBoundingClientRect();
    const rootMargin = Number.parseInt(this._observerOptions.rootMargin);

    const isIntersecting =
      itemRect.bottom + rootMargin >= rootRect.top &&
      itemRect.top - rootMargin <= rootRect.bottom &&
      itemRect.right + rootMargin >= rootRect.left &&
      itemRect.left - rootMargin <= rootRect.right;

    this.intersected([{ isIntersecting }]);

    await nextFrame();

    this.instances[0].enter();
  }

  /**
   * Recalcule la position de l'élément après un redimensionnement.
   * @returns {void}
   */
  resized() {
    const container = getOffsetSizes(this.$parent.$refs.container);
    const parent = getOffsetSizes(this.$parent.$el);
    const item = getOffsetSizes(this.$el);

    const relativeX = (item.x - container.x) / container.width;
    const relativeY = (item.y - container.y) / container.height;

    const x = (parent.width - item.width) / 2 - container.width * relativeX;
    const y = (parent.height - item.height) / 2 - container.height * relativeY;

    this.position = { x, y };
  }

  /**
   * Appelé lorsque l'élément intersecte avec le viewport.
   * @param {IntersectionObserverEntry[]} entries Entrées de l'IntersectionObserver.
   * @returns {void}
   */
  intersected(entries) {
    this.$emit('intersect', entries);
    entries.forEach((entry) => {
      const wasVisible = this.isVisible;
      this.isVisible = entry.isIntersecting;
      const areVisible = this.instances.some((item) => item.isVisible);

      if (areVisible && !wasVisible) {
        gsap.set(this.elements, {
          opacity: 1,
        });
      } else if (!areVisible && wasVisible) {
        gsap.set(this.elements, {
          opacity: 0,
        });
      }
    });
  }

  /**
   * Méthode appelée lors de la destruction du composant.
   * Nettoie l'IntersectionObserver.
   * @returns {void}
   */
  destroyed() {
    this.$observer.unobserve(this.$el);
  }

  /**
   * Centre l'élément dans le viewport en ajustant la position du parent.
   * @returns {void}
   */
  center() {
    const { x, y } = this.position;
    const snapToNext = (value, half, offset) =>
      (value >= 0 ? Math.ceil(value / half) : Math.floor(value / half) + 1) * half + offset;

    this.$parent.incrX = snapToNext(this.$parent.incrX, this.$parent.halfX, x);
    this.$parent.xTo(this.$parent.incrX);
    if (this.$parent.mode === 'slider') return;
    this.$parent.incrY = snapToNext(this.$parent.incrY, this.$parent.halfY, y);
    this.$parent.yTo(this.$parent.incrY);
  }

  /**
   * Anime l'élément pour l'agrandir et le mettre en avant (pop in).
   * @returns {Promise<void>} Promise résolue lorsque l'animation est terminée.
   */
  async popIn() {
    this.animation?.kill();
    this.instances.forEach((item) => (item.isActive = true));
    this.elements.forEach(({ style }) => (style.zIndex = 9999));
    await nextFrame();
    await Promise.all([
      ...this.$parent.$children.ArchivesGalleryItem.filter(
        (item) => item.isActive && item.$options.index !== this.$options.index,
      ).map((item) => item.popOut()),
      new Promise((resolve) => {
        this.animation = gsap.to(this.elements, {
          x: 0,
          y: 0,
          scale: 1.8,
          rotation: this.$parent.mode === 'random' ? Math.random() * 16 - 8 : 0,
          duration: 0.6,
          ease: 'power4',
          onComplete: resolve,
        });
      }),
    ]);
  }

  /**
   * Anime l'élément pour le réduire et le remettre à sa position normale (pop out).
   * @returns {Promise<void>} Promise résolue lorsque l'animation est terminée.
   */
  popOut() {
    this.animation?.kill();
    this.instances.forEach((item) => (item.isActive = false));
    return new Promise((resolve) => {
      const anotherItemIsActive = this.$parent.$children.ArchivesGalleryItem.some(
        (item) => item.isActive && item.$options.index !== this.$options.index,
      );

      let x = 0;
      let y = 0;
      let rotation = 0;

      if (this.$parent.mode === 'random') {
        x = Math.random() * 100 - 50;
        y = Math.random() * 100 - 50;
        rotation = Math.random() * 32 - 16;
      }

      const animate = () => {
        this.animation = gsap.to(this.elements, {
          x,
          y,
          rotation,
          scale: 1,
          duration: 0.6,
          ease: 'power4',
          onComplete: () => {
            if (!anotherItemIsActive) {
              this.$parent.availableZIndex += 1;
              this.elements.forEach(({ style }) => (style.zIndex = this.$parent.availableZIndex));
            }
            resolve();
          },
        });
      };

      if (anotherItemIsActive) {
        this.$parent.availableZIndex += 1;
        this.elements.forEach(({ style }) => (style.zIndex = this.$parent.availableZIndex));
        nextFrame().then(animate);
      } else {
        animate();
      }
    });
  }

  /**
   * Gère le clic sur l'élément : agrandit ou réduit selon l'état actuel.
   * @returns {void}
   */
  onClick() {
    if (!this.isActive) {
      this.instances[0].popIn();
    } else {
      this.instances[0].popOut();
    }
    this.center();
    this.$emit('change', this.isActive);
  }
}
